/* Đối chiếu ngân hàng (sổ phụ ↔ sổ tiền gửi PMKT) và đối chiếu thuế (hoá đơn của Hệ thống thuế ↔ bảng kê PMKT), nhập dòng chứng từ từ Excel.
   Port nguyên văn các hàm thuần của app cũ (bản v2): chuẩn hoá số / ngày / số chứng từ, dò dòng tiêu đề, gợi ý ánh xạ cột, ghép và tổng hợp. */
import { dmy } from './dates';
import { fmt } from './money';

import type { Cell, Grid } from './import';

/* ---------- chuẩn hoá giá trị ---------- */
export const rxNorm = (s: unknown): string => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase().replace(/\s+/g, ' ').trim();
/** Ngoại tệ (USD, EUR, THB…) hiển thị 2 số lẻ. */
export const rxFmt = (n: unknown, cur?: string): string => fmt(n as number, cur && cur !== 'VND' ? 'USD' : 'VND');
export const rxRnd = (x: unknown): number => Math.round((+(x as number) || 0) * 100) / 100;
export const rxCol = (i: number): string => { let s = ''; i++; while (i) { const r = (i - 1) % 26; s = String.fromCharCode(65 + r) + s; i = Math.floor((i - 1) / 26); } return s; };

/** Số tiền từ ô Excel / chữ: nhận cả 1.234,56 và 1,234.56, số âm trong ngoặc hoặc có dấu trừ. */
export function rxNum(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  let s = String(v).trim();
  if (!s) return 0;
  const neg = /^\(.*\)$/.test(s) || /^-/.test(s) || /-$/.test(s);
  s = s.replace(/[^\d.,]/g, '');
  if (!/\d/.test(s)) return 0;
  const lc = s.lastIndexOf(','), ld = s.lastIndexOf('.');
  if (lc >= 0 && ld >= 0) { const dec = lc > ld ? ',' : '.'; s = s.split(dec === ',' ? '.' : ',').join(''); if (dec === ',') s = s.replace(',', '.'); }
  else if (lc >= 0 || ld >= 0) { const parts = s.split(lc >= 0 ? ',' : '.'); s = parts.length > 2 || parts[parts.length - 1].length === 3 ? parts.join('') : parts.join('.'); }
  const n = parseFloat(s);
  return isNaN(n) ? 0 : (neg ? -n : n);
}

export function rxDate(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'number') return v > 20000 && v < 80000 ? new Date(Math.round((v - 25569) * 864e5)).toISOString().slice(0, 10) : '';
  const s = String(v).trim();
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/))) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  if ((m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/))) return `${m[3].length === 2 ? '20' + m[3] : m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  return '';
}

export const rxRef = (v: unknown): string => (v === null || v === undefined ? '' : typeof v === 'number' ? String(Math.round(v * 1e6) / 1e6) : String(v).toUpperCase().replace(/\s+/g, ''));
/** Số hoá đơn: số nguyên, bỏ các số 0 ở đầu (00000123 → 123). */
export function rxInv(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'number') return isFinite(v) ? String(Math.round(Math.abs(v))) : '';
  const d = String(v).replace(/\D/g, '');
  return d ? (d.replace(/^0+/, '') || '0') : '';
}
export const rxMst = (v: unknown): string => (v === null || v === undefined ? '' : typeof v === 'number' ? String(Math.round(v)).padStart(10, '0') : String(v).replace(/\s+/g, '').trim());
export const rxMstKey = (v: unknown): string => String(v || '').replace(/[^0-9A-Za-z]/g, '').replace(/^0+/, '');
export const rxSer = (v: unknown): string => rxNorm(v).replace(/[^a-z0-9]/g, '').toUpperCase();

/* ---------- dò dòng tiêu đề, gợi ý ánh xạ cột ---------- */
export interface RxField { k: string; l: string; req?: number; t: 'ref' | 'date' | 'text' | 'num' | 'inv' | 'mst'; syn: string[]; not?: string[] }

export const rxSyn = (l: string, s: string): boolean => (s.startsWith('=') ? l === s.slice(1) : l.includes(s));

export function rxHeadAt(rows: Grid, h: number): { labels: string[]; sub: boolean; start: number } {
  const a = rows[h] || [], b = rows[h + 1] || [];
  const ok = (x: Cell) => x !== null && x !== undefined && x !== '', ne = b.filter(ok);
  const sub = a.filter(ok).length >= 2 && ne.length >= 2 && ne.every(x => typeof x === 'string' && !/^[\d\s.,/()-]+$/.test(x) && x.length > 1);
  const n = Math.max(a.length, sub ? b.length : 0), labels: string[] = [];
  for (let i = 0; i < n; i++) labels.push([a[i], sub ? b[i] : null].filter(ok).map(String).join(' – '));
  return { labels, sub, start: h + 1 + (sub ? 1 : 0) };
}

export function rxFindHead(rows: Grid, F: RxField[]): number {
  let best = 0, score = -1;
  for (let i = 0; i < Math.min(rows.length, 60); i++) {
    if ((rows[i] || []).filter(x => x !== null && x !== undefined && x !== '').length < 2) continue;
    const L = rxHeadAt(rows, i).labels.map(rxNorm);
    let sc = 0;
    for (const f of F) if (L.some(l => l && f.syn.some(s => rxSyn(l, s)) && !(f.not || []).some(x => l.includes(x)))) sc += f.req ? 3 : 1;
    if (sc > score) { score = sc; best = i; }
  }
  return best;
}

/** Gợi ý cột cho từng trường: ưu tiên ánh xạ đã nhớ (theo tên cột chuẩn hoá), sau đó theo từ đồng nghĩa. */
export function rxGuess(labels: string[], F: RxField[], saved?: Record<string, string> | null): Record<string, number> {
  const L = labels.map(rxNorm), used = new Set<number>(), map: Record<string, number> = {};
  if (saved) for (const f of F) { const i = saved[f.k] ? L.indexOf(saved[f.k]) : -1; if (i >= 0 && !used.has(i)) { map[f.k] = i; used.add(i); } }
  for (const f of F) {
    if (map[f.k] !== undefined) continue;
    for (const s of f.syn) {
      const i = L.findIndex((l, k) => l && !used.has(k) && rxSyn(l, s) && !(f.not || []).some(x => l.includes(x)));
      if (i >= 0) { map[f.k] = i; used.add(i); break; }
    }
  }
  return map;
}

export type RxRec = Record<string, string | number> & { row: number };

/** Đọc các dòng dữ liệu của một sheet theo ánh xạ cột. */
export function rxRecs(rows: Grid, start: number, map: Record<string, number>, F: RxField[]): RxRec[] {
  const out: RxRec[] = [];
  for (let i = start; i < rows.length; i++) {
    const r = rows[i] || [], ne = r.filter(x => x !== null && x !== '');
    if (!ne.length || ne.every(x => String(x).trim().length <= 4)) continue;      // dòng trống / dòng đánh số cột (1) (2) (3)…
    const o: RxRec = { row: i + 1 };
    for (const f of F) {
      const c = map[f.k], v = c === undefined || c < 0 ? null : r[c];
      o[f.k] = f.t === 'num' ? rxNum(v) : f.t === 'date' ? rxDate(v) : f.t === 'inv' ? rxInv(v) : f.t === 'ref' ? rxRef(v) : f.t === 'mst' ? rxMst(v) : (v === null || v === undefined ? '' : String(v).trim());
    }
    out.push(o);
  }
  return out;
}

/** Nhãn cột chuẩn hoá của ánh xạ hiện tại, để nhớ cho lần sau. */
export function rxMapNames(labels: string[], map: Record<string, number>): Record<string, string> {
  const L = labels.map(rxNorm), m: Record<string, string> = {};
  for (const [k, i] of Object.entries(map)) if (i >= 0 && L[i]) m[k] = L[i];
  return m;
}

/* =====================================================================
   ĐỐI CHIẾU NGÂN HÀNG
   ===================================================================== */
export const BK_FB: RxField[] = [
  { k: 'no', l: 'Số chứng từ / Số tham chiếu', req: 1, t: 'ref', syn: ['so chung tu', 'so ct', 'so tham chieu', 'so but toan', 'ma giao dich', 'so giao dich', 'ma gd', 'so gd', 'reference', 'ref no', '=ref', 'transaction no', 'trans no', 'so phieu'], not: ['ngay'] },
  { k: 'date', l: 'Ngày giao dịch', t: 'date', syn: ['ngay giao dich', 'ngay hach toan', 'ngay hieu luc', 'ngay gd', 'ngay chung tu', 'transaction date', 'value date', 'posting date', '=date', '=ngay'] },
  { k: 'desc', l: 'Nội dung', t: 'text', syn: ['noi dung', 'dien giai', 'mo ta', 'chi tiet', 'description', 'details', 'remark', 'narrative'] },
  { k: 'in', l: 'Tiền vào (Ghi có / Credit)', t: 'num', syn: ['so tien ghi co', 'ghi co', 'phat sinh co', 'ps co', 'tien vao', 'credit', '=co'], not: ['so du'] },
  { k: 'out', l: 'Tiền ra (Ghi nợ / Debit)', t: 'num', syn: ['so tien ghi no', 'ghi no', 'phat sinh no', 'ps no', 'tien ra', 'debit', '=no'], not: ['so du'] },
  { k: 'amt', l: 'Số tiền ± (khi chỉ có 1 cột)', t: 'num', syn: ['=so tien', '=amount', '=so tien giao dich'] },
];
export const BK_FP: RxField[] = [
  { k: 'no', l: 'Số chứng từ', req: 1, t: 'ref', syn: ['so chung tu', 'so ct', 'chung tu so', 'so phieu', 'so hieu', '=so'], not: ['ngay'] },
  { k: 'date', l: 'Ngày chứng từ / hạch toán', t: 'date', syn: ['ngay chung tu', 'ngay ct', 'ngay hach toan', 'ngay ghi so', '=ngay'] },
  { k: 'desc', l: 'Diễn giải', t: 'text', syn: ['dien giai', 'noi dung', 'trich yeu', 'description'] },
  { k: 'in', l: 'Thu / Gửi vào (PS Nợ TK 112)', t: 'num', syn: ['gui vao', 'phat sinh no', 'ps no', 'so tien no', 'so tien thu', 'tien thu', '=thu', '=no', '=nợ'], not: ['so du', 'ton', 'doi ung'] },
  { k: 'out', l: 'Chi / Rút ra (PS Có TK 112)', t: 'num', syn: ['rut ra', 'phat sinh co', 'ps co', 'so tien co', 'so tien chi', 'tien chi', '=chi', '=co'], not: ['so du', 'ton', 'doi ung'] },
  { k: 'amt', l: 'Số tiền ± (khi chỉ có 1 cột)', t: 'num', syn: ['=so tien', '=amount'] },
];
export const BK_RES: Record<string, [string, string]> = { ok: ['Khớp', 'r-ok'], diff: ['Lệch số tiền', 'r-diff'], bOnly: ['Chỉ có trên sổ phụ', 'r-b'], pOnly: ['Chỉ có trên PMKT', 'r-p'], noref: ['Thiếu số chứng từ', 'r-nr'] };

export interface BkTxn { row: number; no: string; date: string; desc: string; in: number; out: number; amt: number }
export interface BkPrepOpts { from?: string; to?: string; inPer?: boolean }

/** Lọc các dòng giao dịch: bỏ dòng tổng cộng / số dư, bỏ dòng ngoài kỳ (nếu chọn). Số tiền ± (1 cột) tách thành vào / ra. */
export function bkPrep(recs: RxRec[], o: BkPrepOpts): { list: BkTxn[]; skip: number } {
  const from = o.from || '', to = o.to || '';
  let skip = 0;
  const out: BkTxn[] = [];
  for (const r0 of recs) {
    const r = r0 as unknown as BkTxn;
    if (!r.in && !r.out && r.amt) { if (r.amt > 0) r.in = r.amt; else r.out = -r.amt; }
    if (!r.in && !r.out) continue;
    if (!r.no && !r.date) continue;                       // dòng cộng / tổng cộng / số dư
    if (o.inPer && r.date && ((from && r.date < from) || (to && r.date > to))) { skip++; continue; }
    out.push(r);
  }
  return { list: out, skip };
}

export interface BkRow {
  no: string; dB: string; dP: string; ds: string; dp: string; iB: number; iP: number; oB: number; oP: number; nB: number; nP: number; note: string;
  dI: number; dO: number; res: 'ok' | 'diff' | 'bOnly' | 'pOnly' | 'noref'; fl: string;
}

/** Ghép từng số chứng từ của sổ phụ (B) với sổ PMKT (P); cộng gộp nhiều dòng cùng số chứng từ. */
export function bkMatch(B: BkTxn[], P: BkTxn[], tol: number): BkRow[] {
  type Agg = { no: string; date: string; desc: string; in: number; out: number; n: number };
  const grp = (list: BkTxn[], side: string) => {
    const m = new Map<string, Agg>();
    let n0 = 0;
    for (const r of list) {
      const k = r.no || ('#' + side + (++n0));
      let a = m.get(k);
      if (!a) m.set(k, a = { no: r.no, date: r.date, desc: r.desc, in: 0, out: 0, n: 0 });
      a.in += r.in; a.out += r.out; a.n++;
      if (!a.date) a.date = r.date;
      if (!a.desc) a.desc = r.desc;
    }
    return m;
  };
  const mb = grp(B, 'b'), mp = grp(P, 'p'), keys = [...new Set([...mb.keys(), ...mp.keys()])];
  return keys.map(k => {
    const b = mb.get(k), p = mp.get(k);
    const r = { no: (b || p)!.no, dB: b?.date || '', dP: p?.date || '', ds: b?.desc || '', dp: p?.desc || '', iB: rxRnd(b?.in), iP: rxRnd(p?.in), oB: rxRnd(b?.out), oP: rxRnd(p?.out), nB: b?.n || 0, nP: p?.n || 0, note: '' } as BkRow;
    r.dI = rxRnd(r.iB - r.iP); r.dO = rxRnd(r.oB - r.oP);
    r.res = !r.no ? 'noref' : !p ? 'bOnly' : !b ? 'pOnly' : Math.abs(r.dI) <= tol && Math.abs(r.dO) <= tol ? 'ok' : 'diff';
    const fl: string[] = [];
    if (b && p && r.dB && r.dP && r.dB !== r.dP) fl.push('Lệch ngày');
    if (r.nB > 1 || r.nP > 1) fl.push(`Gộp ${r.nB}/${r.nP} dòng`);
    if (r.res === 'noref') fl.push(r.nB ? 'Trên sổ phụ' : 'Trên PMKT');
    r.fl = fl.join(' · ');
    return r;
  }).sort((a, b) => (a.dB || a.dP || '9').localeCompare(b.dB || b.dP || '9') || String(a.no).localeCompare(String(b.no), 'vi', { numeric: true }));
}

export interface BkSum { iB: number; iP: number; oB: number; oP: number; n: number; c: Record<string, number>; a: Record<string, number>; net: number }
export function bkSum(R: BkRow[]): BkSum {
  const s: BkSum = { iB: 0, iP: 0, oB: 0, oP: 0, n: R.length, c: { ok: 0, diff: 0, bOnly: 0, pOnly: 0, noref: 0 }, a: { ok: 0, diff: 0, bOnly: 0, pOnly: 0, noref: 0 }, net: 0 };
  for (const r of R) { s.iB += r.iB; s.iP += r.iP; s.oB += r.oB; s.oP += r.oP; s.c[r.res]++; s.a[r.res] += r.res === 'pOnly' ? r.iP - r.oP : r.res === 'diff' ? r.dI - r.dO : r.iB - r.oB + r.iP - r.oP; }
  s.net = rxRnd((s.iB - s.oB) - (s.iP - s.oP));
  return s;
}

/* =====================================================================
   ĐỐI CHIẾU THUẾ
   ===================================================================== */
export const TX_RES: Record<string, [string, string]> = { ok: ['Khớp', 'r-ok'], diff: ['Lệch số tiền', 'r-diff'], tOnly: ['Chỉ có trên HT thuế', 'r-b'], pOnly: ['Chỉ có trên PMKT', 'r-p'] };

/** Các trường cần đọc của một bên: txT = dữ liệu Hệ thống thuế (có thêm trạng thái hoá đơn), txP = bảng kê PMKT. `kind` = đầu vào (mua vào) hoặc đầu ra (bán ra). */
export function txFields(side: 'txT' | 'txP', kind: 'in' | 'out'): RxField[] {
  const inp = kind === 'in', who = inp ? 'nguoi ban' : 'nguoi mua', whoV = inp ? 'người bán' : 'người mua';
  const f: RxField[] = [
    { k: 'no', l: 'Số hoá đơn', req: 1, t: 'inv', syn: ['so hoa don', 'so hd', 'invoice no', '=so'], not: ['ky hieu', 'mau', 'ki hieu'] },
    { k: 'serial', l: 'Ký hiệu hoá đơn', t: 'text', syn: ['ky hieu hoa don', 'ky hieu hd', 'ki hieu hoa don', '=ky hieu', '=ki hieu', 'serial'], not: ['mau so'] },
    { k: 'date', l: 'Ngày hoá đơn', t: 'date', syn: ['ngay lap', 'ngay hoa don', 'ngay, thang, nam', 'ngay hd', 'ngay phat hanh', '=ngay'] },
    { k: 'mst', l: 'MST ' + whoV, t: 'mst', syn: ['mst ' + who, 'ma so thue ' + who, '=mst', 'mst', 'ma so thue'], not: ['ten'] },
    { k: 'name', l: 'Tên ' + whoV, t: 'text', syn: ['ten ' + who, who, 'ten don vi', 'ten khach hang', 'ten nha cung cap', 'ten doi tac', '=ten'], not: ['mst', 'ma so thue', 'dia chi'] },
    { k: 'pre', l: 'Tiền trước VAT', req: 1, t: 'num', syn: ['tong tien chua thue', 'tien chua thue', 'chua co thue', 'chua thue', 'truoc thue', 'truoc vat', 'doanh so', 'gia tri hhdv', 'thanh tien'], not: ['thue suat'] },
    { k: 'vat', l: 'Tiền VAT', req: 1, t: 'num', syn: ['tong tien thue', 'tien thue gtgt', 'thue gtgt', 'tien thue', 'tien vat', '=vat', '=thue'], not: ['chua', 'suat', 'ma so', 'mst', 'truoc'] },
  ];
  if (side === 'txT') f.push({ k: 'st', l: 'Trạng thái hoá đơn', t: 'text', syn: ['trang thai hoa don', 'trang thai', 'tinh trang'], not: ['ket qua'] });
  return f;
}

export interface TxInv { row: number; no: string; serial: string; date: string; mst: string; name: string; pre: number; vat: number; st?: string }
export function txPrep(recs: RxRec[], o: BkPrepOpts): { list: TxInv[]; skip: number } {
  const from = o.from || '', to = o.to || '';
  let skip = 0;
  const out: TxInv[] = [];
  for (const r0 of recs) {
    const r = r0 as unknown as TxInv;
    if (!r.no) continue;                                  // dòng tổng cộng / không có số hoá đơn
    if (o.inPer && r.date && ((from && r.date < from) || (to && r.date > to))) { skip++; continue; }
    out.push(r);
  }
  return { list: out, skip };
}

export interface TxRow {
  no: string; serial: string; date: string; mst: string; name: string; st: string; tPre: number; pPre: number; tVat: number; pVat: number; dP: string; note: string;
  dPre: number; dVat: number; res: 'ok' | 'diff' | 'tOnly' | 'pOnly'; fl: string;
}

/** Ghép hoá đơn theo số hoá đơn; cùng số thì ghép tiếp theo MST rồi ký hiệu. */
export function txMatch(Tl: TxInv[], Pl: TxInv[], tol: number): TxRow[] {
  type Agg = { no: string; serial: string; date: string; mst: string; name: string; st: string; pre: number; vat: number; n: number };
  const agg = (list: TxInv[]): Agg[] => {
    const m = new Map<string, Agg>();
    for (const r of list) {
      const k = r.no + '|' + rxMstKey(r.mst) + '|' + rxSer(r.serial);
      let a = m.get(k);
      if (!a) m.set(k, a = { no: r.no, serial: r.serial, date: r.date, mst: r.mst, name: r.name, st: r.st || '', pre: 0, vat: 0, n: 0 });
      a.pre += r.pre; a.vat += r.vat; a.n++;
      if (!a.name && r.name) a.name = r.name;
      if (!a.date && r.date) a.date = r.date;
      if (!a.st && r.st) a.st = r.st;
    }
    return [...m.values()];
  };
  const A = agg(Tl), B = agg(Pl), gA = new Map<string, Agg[]>(), gB = new Map<string, Agg[]>();
  A.forEach(x => (gA.get(x.no) || gA.set(x.no, []).get(x.no)!).push(x));
  B.forEach(x => (gB.get(x.no) || gB.set(x.no, []).get(x.no)!).push(x));
  const nos = [...new Set([...gA.keys(), ...gB.keys()])], out: TxRow[] = [];
  const mk = (x: Agg) => rxMstKey(x.mst), sk = (x: Agg) => rxSer(x.serial);
  const row = (t: Agg | null | undefined, p: Agg | null | undefined): TxRow => {
    const r = {
      no: (t || p)!.no, serial: t?.serial || p?.serial || '', date: t?.date || p?.date || '', mst: t?.mst || p?.mst || '', name: t?.name || p?.name || '', st: t?.st || '',
      tPre: rxRnd(t?.pre), pPre: rxRnd(p?.pre), tVat: rxRnd(t?.vat), pVat: rxRnd(p?.vat), dP: p?.date || '', note: '',
    } as TxRow;
    r.dPre = rxRnd(r.tPre - r.pPre); r.dVat = rxRnd(r.tVat - r.pVat);
    r.res = !p ? 'tOnly' : !t ? 'pOnly' : Math.abs(r.dPre) <= tol && Math.abs(r.dVat) <= tol ? 'ok' : 'diff';
    const fl: string[] = [];
    if (t && p) {
      if (mk(t) && mk(p) && mk(t) !== mk(p)) fl.push('Lệch MST (PMKT: ' + p.mst + ')');
      if (t.date && p.date && t.date !== p.date) fl.push('Lệch ngày (PMKT: ' + dmy(p.date) + ')');
      if (sk(t) && sk(p) && !sk(t).endsWith(sk(p)) && !sk(p).endsWith(sk(t))) fl.push('Lệch ký hiệu (PMKT: ' + p.serial + ')');
    }
    if ((t?.n || 0) > 1 || (p?.n || 0) > 1) fl.push(`Gộp ${t?.n || 0}/${p?.n || 0} dòng`);
    if (/huy|thay the|dieu chinh/.test(rxNorm(r.st)) && !/^hoa don moi$/.test(rxNorm(r.st))) fl.push('Lưu ý trạng thái HĐ');
    r.fl = fl.join(' · ');
    return r;
  };
  for (const no of nos) {
    const a = [...(gA.get(no) || [])], b = [...(gB.get(no) || [])];
    const take = (pred: (x: Agg, y: Agg) => boolean) => {
      for (const x of [...a]) { const j = b.findIndex(y => pred(x, y)); if (j >= 0) { out.push(row(x, b[j])); a.splice(a.indexOf(x), 1); b.splice(j, 1); } }
    };
    if (a.length > 1 || b.length > 1) { take((x, y) => !!mk(x) && mk(x) === mk(y) && sk(x) === sk(y)); take((x, y) => !!mk(x) && mk(x) === mk(y)); take((x, y) => !!sk(x) && sk(x) === sk(y)); }
    if (a.length === 1 && b.length === 1) { out.push(row(a.pop(), b.pop())); }
    a.forEach(x => out.push(row(x, null)));
    b.forEach(y => out.push(row(null, y)));
  }
  return out.sort((x, y) => (x.date || '9').localeCompare(y.date || '9') || (+x.no - +y.no) || String(x.mst).localeCompare(String(y.mst)));
}

export interface TxSum { n: number; tPre: number; pPre: number; tVat: number; pVat: number; c: Record<string, number>; dPre: number; dVat: number }
export function txSum(R: TxRow[]): TxSum {
  const s: TxSum = { n: R.length, tPre: 0, pPre: 0, tVat: 0, pVat: 0, c: { ok: 0, diff: 0, tOnly: 0, pOnly: 0 }, dPre: 0, dVat: 0 };
  for (const r of R) { s.tPre += r.tPre; s.pPre += r.pPre; s.tVat += r.tVat; s.pVat += r.pVat; s.c[r.res]++; }
  s.dPre = rxRnd(s.tPre - s.pPre); s.dVat = rxRnd(s.tVat - s.pVat);
  return s;
}

/* =====================================================================
   NHẬP DÒNG CHI TIẾT CHỨNG TỪ TỪ EXCEL
   ===================================================================== */
export function impFields(t: string): RxField[] {
  if (t === 'cktm') return [
    { k: 'desc', l: 'Vé hãng', req: 1, t: 'text', syn: ['ve hang', 'hang bay', '=hang', 'dien giai', 'description', 'noi dung'] },
    { k: 'price', l: 'Số tiền', req: 1, t: 'num', syn: ['so tien', 'amount', 'thanh tien'] },
    { k: 'unit', l: 'Ghi chú', t: 'text', syn: ['ghi chu', 'note', 'remark'] }];
  if (t === 'invoice' || t === 'debit') return [
    { k: 'desc', l: 'Description', req: 1, t: 'text', syn: ['description', 'dien giai', 'noi dung'] },
    { k: 'unit', l: 'UOM', t: 'text', syn: ['uom', 'unit', 'dvt', 'don vi tinh'], not: ['price', 'gia'] },
    { k: 'qty', l: 'Quantity', t: 'num', syn: ['quantity', '=qty', 'so luong', '=sl'] },
    { k: 'price', l: 'Unit Price', t: 'num', syn: ['unit price', 'don gia', '=price'] },
    { k: 'amt', l: 'Amount', t: 'num', syn: ['=amount', 'thanh tien', 'so tien'] }];
  return [
    { k: 'desc', l: 'Diễn giải', req: 1, t: 'text', syn: ['dien giai', 'noi dung', 'description', 'hang muc'] },
    { k: 'unit', l: 'ĐVT', t: 'text', syn: ['dvt', 'don vi tinh', 'uom', 'unit'], not: ['price', 'gia'] },
    { k: 'qty', l: 'Số lượng', t: 'num', syn: ['so luong', '=sl', 'quantity', '=qty'] },
    { k: 'price', l: 'Đơn giá', t: 'num', syn: ['don gia', 'unit price', '=price'] },
    { k: 'amt', l: 'Thành tiền', t: 'num', syn: ['thanh tien', '=so tien', '=amount'] },
    { k: 'vat', l: 'Thuế suất VAT', t: 'text', syn: ['thue suat', '=vat', 'vat (%)', 'thue gtgt'], not: ['tien thue'] }];
}

/** File mẫu nhập dòng: [tiêu đề cột, độ rộng cột, ghi chú]. */
export const IMP_TPL: Record<'cktm' | 'en' | 'vi', [string[], number[], string]> = {
  cktm: [['Vé hãng', 'Số tiền', 'Ghi chú'], [30, 18, 30], 'Mỗi dòng 1 hãng. Số tiền chiết khấu nhập số (không ghi đ/VND).'],
  en: [['Description', 'UOM', 'Quantity', 'Unit Price'], [50, 12, 12, 18], 'One line per item, fill from row 7. Amount = Quantity × Unit Price.'],
  vi: [['Diễn giải', 'ĐVT', 'Số lượng', 'Đơn giá', 'Thuế suất VAT (0/5/8/10/KCT)'], [50, 12, 12, 18, 18], 'Điền từ dòng 7, mỗi dòng 1 nội dung. Thành tiền = Số lượng × Đơn giá. Thuế suất để trống = 10%.'],
};
export const impTplOf = (t: string): [string[], number[], string] => (t === 'cktm' ? IMP_TPL.cktm : (t === 'invoice' || t === 'debit') ? IMP_TPL.en : IMP_TPL.vi);

export function impVat(v: unknown, def: string): string {
  if (v === null || v === undefined || v === '') return def;
  if (typeof v === 'number') { const n = v > 0 && v < 1 ? Math.round(v * 100) : Math.round(v); return ['0', '5', '8', '10'].includes(String(n)) ? String(n) : def; }
  const s = rxNorm(v);
  if (/kct|khong chiu/.test(s)) return 'kct';
  if (s === 'na' || s === '-' || s === '—' || /khong (ap dung|tinh)/.test(s)) return 'na';
  const n = parseFloat(s.replace(',', '.'));
  if (isNaN(n)) return def;
  return impVat(n, def);
}

export interface ImpItem { desc: string; unit: string; qty: number; price: number; vat: string }
export type ImpResult = { items: ImpItem[] } | { error: 'cols'; field: string } | { error: 'empty' };

/** Đọc các dòng chi tiết từ các sheet của file Excel cho loại chứng từ `t` (chọn sheet khớp cột nhất). */
export function parseImportItems(sheets: { name?: string; rows: Grid }[], t: string): ImpResult {
  const F = impFields(t), def = ['invoice', 'debit', 'cktm'].includes(t) ? 'na' : '10';
  let best: { sh: { rows: Grid }; hd: ReturnType<typeof rxHeadAt>; map: Record<string, number>; sc: number } | null = null;
  for (const sh of sheets) {
    const h = rxFindHead(sh.rows, F), hd = rxHeadAt(sh.rows, h), map = rxGuess(hd.labels, F);
    const sc = F.filter(f => map[f.k] >= 0).length + (map.desc >= 0 ? 5 : 0);
    if (!best || sc > best.sc) best = { sh, hd, map, sc };
  }
  if (!best || !(best.map.desc >= 0) || !(best.map.price >= 0 || best.map.amt >= 0)) return { error: 'cols', field: F[0].l };
  const { sh, hd, map } = best, get = (r: Cell[], k: string): Cell => (map[k] >= 0 ? r[map[k]] : null), out: ImpItem[] = [];
  for (let i = hd.start; i < sh.rows.length; i++) {
    const r = sh.rows[i] || [], desc = String(get(r, 'desc') ?? '').trim();
    if (!desc) continue;
    if (/^(tong cong|cong|tong|total|grand total|sub ?total)\b/.test(rxNorm(desc))) continue;
    let qty = map.qty >= 0 ? rxNum(get(r, 'qty')) : 0, price = map.price >= 0 ? rxNum(get(r, 'price')) : 0;
    const amt = map.amt >= 0 ? rxNum(get(r, 'amt')) : 0;
    if (!qty) qty = 1;
    if (!price && amt) price = Math.round(amt / qty * 100) / 100;
    if (t === 'cktm') qty = 1;
    out.push({ desc, unit: String(get(r, 'unit') ?? '').trim(), qty, price, vat: map.vat >= 0 ? impVat(get(r, 'vat'), def) : def });
  }
  return out.length ? { items: out } : { error: 'empty' };
}
